import { ALL_VALUE, INVALID_VALUE } from '../API/ItemsGetters'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'

/**
 * Add-user CXS scope: exactly one concrete permissions partition (not all/none).
 *
 * @param partitionIds - Value of the permissions partition field (array of partition IDs in TelSys).
 * @returns True when CXS subscription may be offered for this scope.
 */
export function isCxsSubscriptionScopeAllowedForAddUser(partitionIds: Nullable<number[]> | undefined): boolean {
    if (!partitionIds || partitionIds.length !== 1) return false

    const partId: number = partitionIds[0]

    return partId !== ALL_VALUE && partId !== INVALID_VALUE
}

/**
 * Whether the permissions partition field means all partitions: nothing selected, or the {@link ALL_VALUE} sentinel.
 * Several concrete partitions or {@link INVALID_VALUE} do not count.
 *
 * @param partitionIds - Value of the permissions partition field (array of partition IDs in TelSys).
 * @returns True when scope covers all partitions of the TelSys.
 */
export function isAllPartitionsScope(partitionIds: Nullable<number[]> | undefined): boolean {
    if (!partitionIds || partitionIds.length === 0) return true

    return partitionIds.length === 1 && partitionIds[0] === ALL_VALUE
}

/**
 * Add-user CXS scope for a telephony server type. A single concrete partition is always accepted; MS Teams also
 * accepts all partitions, since the backend ({@code CReqRespUserBase.validateCXSAgainstTelSysAndPartition}) has no
 * partition check for Teams. BroadWorks keeps the single-partition rule because the backend needs the user's
 * partition to be Enterprise-licensed. Several concrete partitions are never accepted.
 *
 * @param partitionIds - Value of the permissions partition field (array of partition IDs in TelSys).
 * @param telSysType - Resolved telephony server type for the user.
 * @returns True when CXS subscription may be offered for this scope.
 */
export function isCxsSubscriptionScopeAllowedForTelSys(
    partitionIds: Nullable<number[]> | undefined,
    telSysType: Nullable<TelSysTypes>
): boolean {
    if (isCxsSubscriptionScopeAllowedForAddUser(partitionIds)) return true

    return telSysType === TelSysTypes.MS_TEAMS && isAllPartitionsScope(partitionIds)
}

/**
 * Whether scope uses exactly one concrete partition.
 *
 * @param partitionIds - Value of the permissions partition field.
 * @returns True when the selected partition is concrete.
 */
export function isCxsSubscriptionScopeAllowedForPermissionsPartition(
    partitionIds: Nullable<number[]> | undefined
): boolean {
    return isCxsSubscriptionScopeAllowedForAddUser(partitionIds)
}

/**
 * Whether the telephony server type supports CXS subscriptions.
 *
 * CX Score is offered for BroadWorks and MS Teams telephony servers only.
 *
 * @param telSysType - Resolved telephony server type for the user.
 * @returns True when CXS subscription may be offered for this telephony server type.
 */
export function isCxsTelSysSupported(telSysType: Nullable<TelSysTypes>): boolean {
    return telSysType === TelSysTypes.BROADSOFT_BROADWORKS || telSysType === TelSysTypes.MS_TEAMS
}

type AddUserCxsApplicabilityPendingArgs = {
    selectedUser?: string
    isCXScoreFeatureEnabled: Nullable<boolean>
    permissionPartitionIds: Nullable<number[]> | undefined
    telSysValue: Nullable<number> | undefined
    telSysType: Nullable<TelSysTypes>
    telSysIntegrationModeOptions?: Nullable<string>
}

/**
 * Whether edit-mode CXS applicability is still being determined.
 *
 * For MS Teams servers the integration type and mode also gate CXS, so applicability stays pending until both
 * have loaded ({@code undefined} means not yet fetched; {@code null} means fetched with no value reported).
 *
 * An all-partitions scope stays pending while the tel-sys type is unknown, since it qualifies only for MS Teams.
 *
 * @param args - Applicability inputs from the add-user modal.
 * @returns True when scope is concrete enough to support CXS, but feature-flag, tel-sys type or Teams
 *     integration data has not loaded yet.
 */
export function isCxsSubscriptionApplicabilityPendingForAddUser({
    selectedUser,
    isCXScoreFeatureEnabled,
    permissionPartitionIds,
    telSysValue,
    telSysType,
    telSysIntegrationModeOptions
}: AddUserCxsApplicabilityPendingArgs): boolean {
    if (!selectedUser) return false

    const isScopeAllowed: boolean =
        isCxsSubscriptionScopeAllowedForTelSys(permissionPartitionIds, telSysType) ||
        (telSysType === TelSysTypes.UNKNOWN && isAllPartitionsScope(permissionPartitionIds))

    if (!isScopeAllowed) return false

    if (!telSysValue || telSysValue === ALL_VALUE || telSysValue === INVALID_VALUE) return false

    if (isCXScoreFeatureEnabled === false) return false

    return (
        isCXScoreFeatureEnabled === undefined ||
        telSysType === TelSysTypes.UNKNOWN ||
        // CXS on MS Teams hinges on the business integration options, which load with the telsys.
        (telSysType === TelSysTypes.MS_TEAMS && telSysIntegrationModeOptions === undefined)
    )
}
